import type { SkillDef } from '@nine/shared';
import { INK, Layer } from './grid';
import { KIND_RIM, type MoveKind } from './plate';

/**
 * The badge in a move icon's lower right: the one mechanic that sets the move apart (a status it
 * applies or gives, a dash, a multi-hit, a stun, a volley, an area). Colour carries the meaning,
 * the 3x3 shape separates what colour alone would not.
 */
export type Chip =
  | 'bleed' | 'poison' | 'chill' | 'slow' | 'charm' | 'silence' | 'shackle' | 'sunder' | 'haste' | 'ward'
  | 'focus' | 'warcry' | 'bell' | 'frenzy' | 'trance' | 'dash' | 'hits' | 'lifesteal' | 'stun' | 'volley' | 'aoe';

const CHIPS: Readonly<Record<Chip, { c: string; g: readonly string[] }>> = {
  bleed: { c: '#ff4a52', g: ['.X.', 'XXX', '.X.'] },
  poison: { c: '#9ce04a', g: ['XXX', 'XXX', '.X.'] },
  chill: { c: '#bfeaff', g: ['X.X', '.X.', 'X.X'] },
  slow: { c: '#4fb0f0', g: ['XXX', '.X.', '...'] },
  charm: { c: '#ff8ac0', g: ['X.X', 'XXX', '.X.'] },
  silence: { c: '#c8c8d0', g: ['XXX', '...', 'XXX'] },
  shackle: { c: '#a9b6c8', g: ['XXX', 'X.X', 'XXX'] },
  sunder: { c: '#f59a2c', g: ['X..', '.X.', '..X'] },
  haste: { c: '#ffffff', g: ['.X.', 'XXX', 'X.X'] },
  ward: { c: '#8ad0ff', g: ['XXX', 'XXX', '.X.'] },
  focus: { c: '#c08aff', g: ['.X.', 'XXX', '.X.'] },
  warcry: { c: '#f6cf4a', g: ['X.X', 'X.X', '.X.'] },
  bell: { c: '#f6cf4a', g: ['.X.', 'XXX', 'X.X'] },
  frenzy: { c: '#ff8a2a', g: ['.X.', 'XXX', 'XXX'] },
  trance: { c: '#c08aff', g: ['XX.', '.X.', '.XX'] },
  dash: { c: '#ffffff', g: ['XX.', '.XX', 'XX.'] },
  hits: { c: '#f6cf4a', g: ['X.X', '...', 'X.X'] },
  lifesteal: { c: '#ff4a52', g: ['X.X', 'XXX', '.X.'] },
  stun: { c: '#ffe45a', g: ['.X.', 'XXX', '.X.'] },
  volley: { c: '#dfe7f2', g: ['X.X', '.X.', 'X..'] },
  aoe: { c: '#f6cf4a', g: ['XXX', 'X.X', 'XXX'] },
};

export function chipOf(s: SkillDef): Chip | null {
  const a = s.applies?.status;
  const m: Record<string, Chip> = {
    bleed: 'bleed', poison: 'poison', chill: 'chill', 'snow-daze': 'chill', slow: 'slow', charm: 'charm',
    silence: 'silence', shackle: 'shackle', sunder: 'sunder',
  };
  if (a && m[a]) return m[a]!;
  const f = s.self?.status;
  const sm: Record<string, Chip> = {
    haste: 'haste', ward: 'ward', focus: 'focus', 'war-cry': 'warcry', 'iron-bell': 'bell',
    'blood-frenzy': 'frenzy', trance: 'trance', 'snow-pride': 'ward',
  };
  if (f && sm[f]) return sm[f]!;
  if (s.lifesteal) return 'lifesteal';
  if (s.dash || s.blink) return 'dash';
  if ((s.hits ?? 0) > 1) return 'hits';
  if (s.stun || s.hitStun) return 'stun';
  if (s.volley) return 'volley';
  if (s.aoe || s.spin) return 'aoe';
  return null;
}

/** Draws the badge (a dark disc, a ring of the chip colour, the glyph) at the plate's lower right. */
export function drawChip(L: Layer, chip: Chip): void {
  const { c, g } = CHIPS[chip];
  const cx = 18.5;
  const cy = 18.5;
  if (L.detail === 0) {
    L.rect(17, 17, 3, 3, INK);
    L.rect(18, 18, 2, 2, c);
    return;
  }
  L.disc(cx, cy, 3.7, INK);
  L.disc(cx, cy, 3, c);
  L.sprite(17, 17, g, { X: INK });
}

const GEMS: Readonly<Record<MoveKind, readonly string[]>> = {
  attack: ['XXX', 'XXX', 'XXX'],
  break: ['X.X', '.X.', 'X.X'],
  power: ['XXX', 'X.X', 'XXX'],
  heal: ['.X.', 'XXX', '.X.'],
  buff: ['.X.', 'XXX', 'X.X'],
};

/** Square plates: the kind's gem in the upper-left corner (a mark that reads without colour). */
export function drawGem(L: Layer, kind: MoveKind): void {
  if (L.detail === 0) {
    L.rect(2, 2, 3, 3, INK);
    L.rect(3, 3, 1, 1, KIND_RIM[kind]);
    return;
  }
  L.rect(2, 2, 5, 5, INK);
  L.sprite(3, 3, GEMS[kind], { X: KIND_RIM[kind] });
}
